import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Checkbox, Message } from 'src/components/atoms';
import { MessageAuthorAndTime } from 'src/components/atoms/MessageAuthorAndTime';
import { useTheme } from 'styled-components';
import {
  checkIsSameAuthor,
  getCurrentMessageTimestamp,
  getNextMessageTimestamp,
  getPreviousMessageTimestamp,
  MessageOrChannelEntry,
} from 'src/utils/messagesHelpers';
import { isSameTime, TimeUnit } from 'src/utils/time';
import {
  DateOrNotification,
  MessageAndCheckboxContainer,
  MessageBox,
  MessageContainer,
  MessagesListContainer,
  Minute,
} from './ReportMessages.styles';
import { formatMessageDate } from './formatMessageDate';

interface Props {
  messagesList: MessageOrChannelEntry[];
  allowMessageSelect?: boolean;
  selectedMessages: string[];
  onMessageSelect: (messageId: string) => void;
}
export const MessageDeletionList = ({
  messagesList,
  allowMessageSelect,
  selectedMessages,
  onMessageSelect,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isVaultVariant = theme.isVaultVariant;
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    !allowMessageSelect && scrollToBottom();
  }, [messagesList.length, allowMessageSelect]);

  return (
    <MessagesListContainer data-testid="case-messages-all" removePadding>
      {messagesList.map((message, index) => {
        const current = getCurrentMessageTimestamp(index, messagesList);
        const previous = getPreviousMessageTimestamp(index, messagesList);
        const next = getNextMessageTimestamp(index, messagesList);
        const sameAuthor = checkIsSameAuthor(index, messagesList);
        const showDate = !isSameTime(current, previous, TimeUnit.days);
        const isSameMinute =
          isSameTime(current, next, TimeUnit.minutes) && sameAuthor;
        const showMinute = !isSameMinute;
        const date = formatMessageDate(current);

        if (message.type === 'notification') {
          return (
            <React.Fragment key={message.id}>
              {showDate ? (
                <DateOrNotification data-testid="message-date">
                  <Badge
                    text={date}
                    variant={'custom'}
                    background-color={
                      isVaultVariant
                        ? theme.newPalette.neutral.N400
                        : theme.atlas.semantic.color.outline.default
                    }
                  />
                </DateOrNotification>
              ) : null}
              <DateOrNotification data-testid="chat-notification">
                <Badge
                  text={message.value}
                  variant={'custom'}
                  background-color={
                    isVaultVariant
                      ? theme.newPalette.neutral.N400
                      : theme.atlas.semantic.color.outline.default
                  }
                />
              </DateOrNotification>
            </React.Fragment>
          );
        }

        return (
          <MessageContainer key={message.id}>
            {showDate ? (
              <DateOrNotification data-testid="message-date">
                <Badge
                  text={date}
                  variant={'custom'}
                  background-color={
                    isVaultVariant
                      ? theme.newPalette.neutral.N400
                      : theme.atlas.semantic.color.outline.default
                  }
                />
              </DateOrNotification>
            ) : null}

            <MessageAndCheckboxContainer>
              {allowMessageSelect && (
                <Checkbox
                  checked={
                    message.deletedAt !== null ||
                    selectedMessages.includes(message.id)
                  }
                  onChange={() => onMessageSelect(message.id)}
                  disabled={message.deletedAt !== null}
                  data-testid={`message-checkbox-${index}`}
                  $round
                />
              )}
              <MessageBox
                isReporter={message.isReporter}
                sameAuthor={sameAuthor}
              >
                <Message
                  variant={message.isReporter ? 'received' : 'sent'}
                  text={
                    message.deletedAt
                      ? t('public.ChatContainer.deletedMessageLabel')
                      : message.value
                  }
                  deleted={Boolean(message.deletedAt)}
                  data-testid={
                    message.isReporter ? 'received-message' : 'sent-message'
                  }
                />

                {showMinute || message.deletedAt ? (
                  <Minute isReporter={message.isReporter}>
                    <MessageAuthorAndTime
                      timestamp={message.deletedAt || message.createdAt}
                      author={message.author ?? ''}
                      translated={message.translated}
                      deleted={Boolean(message.deletedAt)}
                      deletedBy={message.deletedBy ?? ''}
                    />
                  </Minute>
                ) : null}
              </MessageBox>
            </MessageAndCheckboxContainer>
          </MessageContainer>
        );
      })}
      <div ref={messagesEndRef} />
    </MessagesListContainer>
  );
};
